import { useState, useEffect } from 'react';
import { Shield, Cookie, Settings, Check, X, Lock, BarChart3, MessageSquare, Users, Download, Trash2, FileText, Info } from 'lucide-react';
import { useConsentManager, ConsentPreferences } from '@/hooks/useConsentManager';
import { Link } from 'react-router-dom';

interface ConsentBannerProps {
  onConsentChange?: (preferences: ConsentPreferences) => void;
  privacyPolicyUrl?: string;
}

const COOKIE_CATEGORIES = {
  necessary: {
    title: 'Necessários',
    description: 'Essenciais para o funcionamento. Não podem ser desabilitados.',
    icon: Lock,
    examples: ['Autenticação', 'Sessão', 'Segurança'],
    required: true,
  },
  analytics: {
    title: 'Analíticos',
    description: 'Nos ajudam a entender como você usa o site.',
    icon: BarChart3,
    examples: ['Google Analytics', 'Heatmaps', 'Performance'],
    required: false,
  },
  marketing: {
    title: 'Marketing',
    description: 'Personalizam anúncios e medem campanhas.',
    icon: MessageSquare,
    examples: ['Facebook Pixel', 'Google Ads', 'Retargeting'],
    required: false,
  },
  personalization: {
    title: 'Personalização',
    description: 'Lembram suas preferências de interface.',
    icon: Users,
    examples: ['Tema', 'Recomendações', 'Interface adaptada'],
    required: false,
  },
} as const;

export function ConsentBanner({
  onConsentChange,
  privacyPolicyUrl = '/politica-privacidade',
}: ConsentBannerProps) {
  const {
    preferences,
    showBanner,
    isInitialized,
    acceptAll,
    acceptNecessaryOnly,
    savePreferences,
    exportConsentData,
    revokeAll,
  } = useConsentManager({ onConsentChange });

  const [showSettings, setShowSettings] = useState(false);
  const [tempPreferences, setTempPreferences] = useState<ConsentPreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
    personalization: false,
    timestamp: Date.now(),
    version: '1.0.0',
  });

  useEffect(() => {
    if (preferences) setTempPreferences(preferences);
  }, [preferences]);

  if (!isInitialized || !showBanner) return null;

  const updateTempPreference = (
    category: keyof Omit<ConsentPreferences, 'timestamp' | 'version'>,
    value: boolean
  ) => {
    if (category === 'necessary') return;
    setTempPreferences(prev => ({ ...prev, [category]: value }));
  };

  return (
    <>
      {/* Banner principal — depende do navegador de quem visita, fica fora do prerender */}
      <div data-prerender-skip className="fixed bottom-0 left-0 right-0 z-50 border-t border-pulse/30 bg-ink/95 backdrop-blur-md">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 shrink-0 rounded-sm bg-pulse/10 p-1.5">
                <Shield className="h-4 w-4 text-pulse" />
              </div>
              <div>
                <p className="font-space text-sm font-semibold text-paper">
                  Proteção de Dados — LGPD
                </p>
                <p className="mt-0.5 text-xs text-bone">
                  Scripts de terceiros são <span className="text-pulse font-medium">realmente bloqueados</span> até você consentir.{' '}
                  <Link to={privacyPolicyUrl} className="text-paper/70 underline underline-offset-2 hover:text-paper transition-colors">
                    Política de Privacidade
                  </Link>
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => setShowSettings(true)}
                className="text-xs text-bone hover:text-paper transition-colors underline underline-offset-2"
              >
                Configurar
              </button>
              <button
                onClick={acceptNecessaryOnly}
                className="rounded-sm border border-bone/30 bg-transparent px-3 py-1.5 text-xs font-medium text-bone hover:border-paper/50 hover:text-paper transition-all"
              >
                Só Necessários
              </button>
              <button
                onClick={acceptAll}
                className="rounded-sm bg-pulse px-3 py-1.5 text-xs font-semibold text-paper hover:bg-pulse-deep transition-colors"
              >
                Aceitar Tudo
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de configurações */}
      {showSettings && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-ink/80 backdrop-blur-sm" onClick={() => setShowSettings(false)} />
          <div className="relative w-full max-w-lg mx-4 mb-0 sm:mb-auto rounded-t-xl sm:rounded-xl border border-bone/20 bg-ink-2 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-bone/20 px-6 py-4">
              <div className="flex items-center gap-2">
                <Settings className="h-4 w-4 text-pulse" />
                <span className="font-space text-sm font-semibold text-paper">Configurações de Privacidade</span>
              </div>
              <button onClick={() => setShowSettings(false)} className="text-bone hover:text-paper transition-colors">
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Body */}
            <div className="max-h-[60vh] overflow-y-auto px-6 py-4 space-y-4">
              <p className="text-xs text-bone">
                Scripts são <span className="text-pulse font-medium">realmente bloqueados</span> até você consentir. Cookies existentes são removidos ao revogar.
              </p>

              {Object.entries(COOKIE_CATEGORIES).map(([key, category]) => {
                const CategoryIcon = category.icon;
                const isChecked = tempPreferences[key as keyof ConsentPreferences] as boolean;

                return (
                  <div key={key} className="flex items-start gap-3 rounded-sm border border-bone/10 bg-ink/50 p-3">
                    <div className="mt-0.5">
                      <input
                        type="checkbox"
                        id={`consent-${key}`}
                        checked={isChecked}
                        disabled={category.required}
                        onChange={(e) =>
                          updateTempPreference(
                            key as keyof Omit<ConsentPreferences, 'timestamp' | 'version'>,
                            e.target.checked
                          )
                        }
                        className="accent-pulse h-3.5 w-3.5 cursor-pointer disabled:cursor-not-allowed"
                      />
                    </div>
                    <label htmlFor={`consent-${key}`} className="cursor-pointer flex-1">
                      <div className="flex items-center gap-2">
                        <CategoryIcon className="h-3.5 w-3.5 text-bone shrink-0" />
                        <span className="text-xs font-semibold text-paper">{category.title}</span>
                        {category.required && (
                          <span className="rounded-sm bg-bone/20 px-1 py-0.5 text-[10px] text-bone">Obrigatório</span>
                        )}
                      </div>
                      <p className="mt-1 text-[11px] text-bone/70">{category.description}</p>
                      <p className="mt-0.5 text-[10px] text-bone/50">{category.examples.join(' · ')}</p>
                    </label>
                  </div>
                );
              })}

              <div className="rounded-sm border border-pulse/20 bg-pulse/5 p-3">
                <div className="flex items-start gap-2">
                  <Info className="h-3.5 w-3.5 text-pulse shrink-0 mt-0.5" />
                  <div className="text-[11px] text-bone/80">
                    <span className="text-paper font-medium">Seus direitos (LGPD): </span>
                    Acesso, correção, exclusão e portabilidade dos seus dados.{' '}
                    <span className="text-bone/60">contato@johnnyvaz.com.br</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-bone/20 px-6 py-4">
              <div className="flex gap-2">
                {preferences !== null && (
                  <>
                    <button
                      onClick={exportConsentData}
                      className="flex items-center gap-1 text-[11px] text-bone hover:text-paper transition-colors"
                    >
                      <Download className="h-3 w-3" />
                      Exportar
                    </button>
                    <button
                      onClick={() => { revokeAll(); setShowSettings(false); }}
                      className="flex items-center gap-1 text-[11px] text-red-400 hover:text-red-300 transition-colors"
                    >
                      <Trash2 className="h-3 w-3" />
                      Revogar
                    </button>
                  </>
                )}
                <Link
                  to={privacyPolicyUrl}
                  className="flex items-center gap-1 text-[11px] text-bone hover:text-paper transition-colors"
                >
                  <FileText className="h-3 w-3" />
                  Política completa
                </Link>
              </div>
              <button
                onClick={() => { savePreferences(tempPreferences); setShowSettings(false); }}
                className="flex items-center gap-1.5 rounded-sm bg-pulse px-4 py-1.5 text-xs font-semibold text-paper hover:bg-pulse-deep transition-colors"
              >
                <Check className="h-3.5 w-3.5" />
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function ConsentStatus({ className = '' }: { className?: string }) {
  const { preferences, revokeAll, exportConsentData } = useConsentManager();

  if (!preferences) return null;

  const activeCount = Object.entries(preferences)
    .filter(([key, value]) => key !== 'timestamp' && key !== 'version' && value)
    .length;

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Shield className="h-3.5 w-3.5 text-pulse" />
      <span className="text-xs text-bone">
        Privacidade: {activeCount}/4 categorias ativas
      </span>
    </div>
  );
}

export default ConsentBanner;
